/**
 * StandardOrdersSection — "Order Again" UI inside customer /profile
 * ─────────────────────────────────────────────────────────────────
 *
 * Groups all saved standard orders by venue (so customer sees their
 * 4 coffee shops + 2 hairdressers + 1 day spa side-by-side). Each card
 * shows: venue name, niche badge, "My usual" name, line items, use_count.
 *
 * Actions per card:
 *   • Use this order  → opens edit modal (name + items: qty / remove)
 *                       then choices: "Use as-is" / "Modify & order"
 *                       sends user to /r/{slug}/menu with seeded cart
 *                       (via localStorage handoff key `seeded_cart_<slug>`)
 *   • Rename / delete
 *
 * Tied to `user.id` from AuthContext. Falls back gracefully when the
 * customer has no saved orders.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Coffee, UtensilsCrossed, Scissors, Sparkles, Trash2, Edit2, Plus, Minus, ShoppingBag, X, Save } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const NICHE_META = {
  Restaurants: { icon: UtensilsCrossed, color: "#0f172a", label: "Restaurant" },
  Medical:     { icon: Sparkles,        color: "#0369a1", label: "Medical" },
  Liquor:      { icon: Coffee,          color: "#7c3aed", label: "Liquor" },
  Tourism:     { icon: Scissors,        color: "#059669", label: "Lifestyle" },
};

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

export default function StandardOrdersSection({ userId }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  function load() {
    if (!userId) return;
    setLoading(true);
    fetch(`${API}/api/directory/standard-orders/${userId}`)
      .then((r) => r.json())
      .then((d) => setOrders(d.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [userId]);

  // Group by venue
  const byVenue = orders.reduce((acc, o) => {
    (acc[o.venue_slug] = acc[o.venue_slug] || { venue_name: o.venue_name, venue_niche: o.venue_niche, venue_slug: o.venue_slug, orders: [] }).orders.push(o);
    return acc;
  }, {});
  const venues = Object.values(byVenue);

  async function deleteOrder(id) {
    if (!window.confirm("Delete this saved order?")) return;
    try {
      await fetch(`${API}/api/directory/standard-orders/${userId}/${id}`, { method: "DELETE" });
      toast.success("Saved order removed");
      load();
    } catch { toast.error("Couldn't delete"); }
  }

  if (loading) return <div className="text-sm text-stone-500">Loading saved orders…</div>;

  if (orders.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-stone-300 p-6 text-center" data-testid="standard-orders-empty">
        <ShoppingBag className="w-8 h-8 text-stone-300 mx-auto mb-2" />
        <p className="text-sm text-stone-600 font-medium">No saved orders yet</p>
        <p className="text-[12px] text-stone-500 mt-1">
          Place an order at any venue and tap <strong>Save as my usual</strong> on the confirmation — it'll appear here for one-tap re-ordering.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="standard-orders-section">
      <div className="space-y-5">
        {venues.map((v) => {
          const meta = NICHE_META[v.venue_niche] || NICHE_META.Restaurants;
          const Icon = meta.icon;
          return (
            <div key={v.venue_slug} data-testid={`standard-orders-venue-${v.venue_slug}`} className="rounded-xl border border-stone-200 overflow-hidden">
              <div className="px-4 py-3 bg-stone-50 border-b border-stone-200 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 rounded-full grid place-items-center shrink-0" style={{ background: meta.color + "1A" }}>
                    <Icon className="w-4 h-4" style={{ color: meta.color }} />
                  </div>
                  <div className="min-w-0">
                    <Link to={`/r/${v.venue_slug}`} className="font-semibold text-stone-900 hover:underline truncate block">{v.venue_name}</Link>
                    <p className="text-[11px] text-stone-500 uppercase tracking-wider">{meta.label}</p>
                  </div>
                </div>
                <Badge variant="outline" className="text-[10px]">{v.orders.length} saved</Badge>
              </div>
              <ul className="divide-y divide-stone-100">
                {v.orders.map((o) => (
                  <li key={o.id} data-testid={`standard-order-${o.id}`} className="px-4 py-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[14px] text-stone-900">{o.name}</p>
                      <p className="text-[12px] text-stone-600 truncate">
                        {o.items.slice(0, 4).map((it) => `${it.qty}× ${it.name}`).join(" · ")}
                        {o.items.length > 4 && ` · +${o.items.length - 4} more`}
                      </p>
                      <p className="text-[11px] text-stone-400 mt-0.5">
                        Used {o.use_count} time{o.use_count === 1 ? "" : "s"}
                      </p>
                    </div>
                    <button
                      data-testid={`standard-order-use-${o.id}`}
                      onClick={() => setEditing(o)}
                      className="shrink-0 px-3 py-1.5 rounded-full bg-stone-900 text-white text-[12px] font-bold hover:bg-stone-700"
                    >
                      Order again
                    </button>
                    <button
                      data-testid={`standard-order-delete-${o.id}`}
                      onClick={() => deleteOrder(o.id)}
                      className="shrink-0 p-1.5 rounded-full text-stone-400 hover:text-red-600 hover:bg-red-50"
                      aria-label="Delete saved order"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <StandardOrderEditModal
        order={editing}
        userId={userId}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

// ─── Edit + Use modal ──────────────────────────────────────────────────────
function StandardOrderEditModal({ order, userId, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!order) return;
    setName(order.name);
    setItems(order.items.map((it) => ({ ...it })));
  }, [order]);

  if (!order) return null;

  const total = items.reduce((s, l) => s + (Number(l.price) || 0) * (l.qty || 1), 0);

  function setQty(i, delta) {
    setItems((arr) => arr.map((it, ii) => ii === i ? { ...it, qty: Math.max(0, (it.qty || 1) + delta) } : it).filter((_, ii) => ii !== i || (arr[i].qty + delta) > 0));
  }
  function removeItem(i) { setItems((arr) => arr.filter((_, ii) => ii !== i)); }

  async function placeOrder(updateMode) {
    if (items.length === 0) { toast.error("Add at least one item"); return; }
    setBusy(true);
    try {
      // 1) Push the order through the venue's pickup-order endpoint
      const orderResp = await fetch(`${API}/api/directory/restaurants/${order.venue_slug}/pickup-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: "Standard customer",
          phone: "0400000000",
          mode: "pickup",
          time: "ASAP",
          items: items.map((it) => ({ name: it.name, qty: it.qty, price: it.price })),
        }),
      });
      if (!orderResp.ok) throw new Error("Couldn't place order");
      const orderData = await orderResp.json();

      // 2) Decide what to do with the saved template
      if (updateMode === "update") {
        await fetch(`${API}/api/directory/standard-orders/${userId}/${order.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, items, increment_use: true }),
        });
        toast.success(`Order #${orderData.order?.id} sent · template updated`);
      } else if (updateMode === "save_new") {
        await fetch(`${API}/api/directory/standard-orders/${userId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            venue_slug: order.venue_slug,
            name: `${name} (variant)`,
            items,
          }),
        });
        toast.success(`Order #${orderData.order?.id} sent · saved as new standard`);
      } else {
        // continue without modifying — just bump use_count
        await fetch(`${API}/api/directory/standard-orders/${userId}/${order.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ increment_use: true }),
        });
        toast.success(`Order #${orderData.order?.id} sent`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.message || "Couldn't place order");
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] p-0 overflow-hidden flex flex-col" data-testid="standard-order-edit-modal">
        <DialogTitle className="sr-only">Order again</DialogTitle>
        <div className="px-5 py-4 border-b border-stone-200 flex items-center justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider font-bold text-stone-500">Order again</p>
            <p className="font-semibold text-stone-900 truncate">{order.venue_name}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-stone-100" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 space-y-4 flex-1">
          {/* Name field */}
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-stone-500 mb-1 block">Order nickname</label>
            <Input data-testid="standard-order-edit-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          {/* Items list */}
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-stone-500 mb-2 block">Items</label>
            <ul className="divide-y divide-stone-100 border border-stone-200 rounded-lg">
              {items.map((it, i) => (
                <li key={i} className="flex items-center gap-3 px-3 py-2.5" data-testid={`standard-order-edit-item-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[14px] text-stone-900 truncate">{it.name}</p>
                    {it.size_chosen && <p className="text-[11px] text-stone-500">Size: {it.size_chosen}</p>}
                    <p className="text-[12px] text-stone-600 tabular-nums">{money(it.price)} each</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      data-testid={`standard-order-edit-dec-${i}`}
                      onClick={() => setQty(i, -1)}
                      className="w-7 h-7 rounded-full border border-stone-200 grid place-items-center hover:bg-stone-50"
                    ><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-6 text-center font-semibold tabular-nums">{it.qty}</span>
                    <button
                      data-testid={`standard-order-edit-inc-${i}`}
                      onClick={() => setQty(i, +1)}
                      className="w-7 h-7 rounded-full border border-stone-200 grid place-items-center hover:bg-stone-50"
                    ><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button
                    onClick={() => removeItem(i)}
                    className="p-1.5 rounded-full text-stone-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                    aria-label="Remove"
                  ><Trash2 className="w-3.5 h-3.5" /></button>
                </li>
              ))}
            </ul>
            {items.length === 0 && (
              <p className="text-[12px] text-stone-500 italic mt-2">All items removed — head to the venue's menu to add new ones.</p>
            )}
          </div>

          {/* Total */}
          <div className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2">
            <span className="text-[12px] uppercase tracking-wider font-bold text-stone-600">Order total</span>
            <span className="text-lg font-bold tabular-nums">{money(total)}</span>
          </div>
        </div>

        {/* Three-way action footer */}
        <div className="border-t border-stone-200 p-4 space-y-2 shrink-0 bg-stone-50">
          <Button
            data-testid="standard-order-place-update"
            onClick={() => placeOrder("update")}
            disabled={busy || items.length === 0}
            className="w-full rounded-full bg-stone-900 text-white hover:bg-stone-700 h-11 font-bold"
          >
            <Save className="w-3.5 h-3.5 mr-2" /> Place order & update my usual
          </Button>
          <div className="grid grid-cols-2 gap-2">
            <Button
              data-testid="standard-order-place-save-new"
              variant="outline"
              onClick={() => placeOrder("save_new")}
              disabled={busy || items.length === 0}
              className="rounded-full h-10 text-[12px] font-semibold"
            >
              Place + save as new
            </Button>
            <Button
              data-testid="standard-order-place-continue"
              variant="outline"
              onClick={() => placeOrder("continue")}
              disabled={busy || items.length === 0}
              className="rounded-full h-10 text-[12px] font-semibold"
            >
              Place without saving
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
